import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionTooltip } from '../../ActionTooltip';
import './Button.scss';

const ButtonThemes = Object.freeze({
    Default: 'default',
    Alert: 'alert',
});

export const Button = ({
    autoFocus,
    children,
    circle,
    className,
    dataTestId,
    disabled,
    disabledTooltip,
    fixedTooltipGapSize,
    icon,
    iconOnly,
    iconSize = '1x',
    iconProps = {},
    isFixedTooltip,
    onBlur,
    onClick,
    rounded,
    stretch,
    style,
    subIcon,
    tabIndex,
    tall,
    theme = 'default',
    tooltip,
    tooltipClassName,
    tooltipHasSetWidth,
    tooltipPosition = 'top',
    tooltipTheme,
    type = 'button',
    variant,
    wrapperClassName,
}) => {
    return (
        <ActionTooltip
            actionType={theme === ButtonThemes.Default ? 'edit' : 'remove'}
            className={classNames('cc-button-tooltip', tooltipClassName)}
            customContent={disabled ? disabledTooltip || tooltip : tooltip}
            position={tooltipPosition}
            isEnabled={!!tooltip || !!disabledTooltip}
            isFixed={isFixedTooltip}
            gapSize={fixedTooltipGapSize}
            shouldUseDisabledOverlay={disabled}
            theme={disabled ? 'alert' : tooltipTheme}
            hasSetWidth={tooltipHasSetWidth}
        >
            <div className={classNames('cc-button-component', wrapperClassName)}>
                <button
                    tabIndex={tabIndex}
                    autoFocus={autoFocus}
                    type={type}
                    data-test-id={dataTestId}
                    disabled={disabled}
                    className={classNames(
                        `${
                            disabled
                                ? 'button button--disabled cursor-not-allowed'
                                : `button button--${variant || 'default'}`
                        }`,
                        {
                            'button--rounded': rounded,
                            'button--circle': circle,
                            'button--stretch': stretch,
                            'button--tall': tall,
                            'button--icon-only': iconOnly,
                            'button--with-tooltip': !!tooltip,
                            'button--theme-default': theme === ButtonThemes.Default,
                            'button--theme-alert': theme === ButtonThemes.Alert,
                        },
                        className,
                    )}
                    style={style}
                    onBlur={onBlur}
                    onClick={onClick}
                >
                    {icon ? (
                        <FontAwesomeIcon
                            icon={icon}
                            size={iconSize}
                            className={classNames('cc-button-component__icon', {
                                'cc-button-component__icon--with-margin': !iconOnly,
                            })}
                            {...iconProps}
                        />
                    ) : null}
                    {subIcon ? (
                        <FontAwesomeIcon
                            icon={subIcon}
                            size="2xs"
                            className="cc-button-component__sub-icon"
                        />
                    ) : null}
                    {children}
                </button>
            </div>
        </ActionTooltip>
    );
};

Button.propTypes = {
    autoFocus: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    variant: PropTypes.oneOf([
        'default',
        'primary',
        'search',
        'dark',
        'light',
        'disabled',
        'alternate',
        'link',
        'transparent',
    ]),
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    disabledTooltip: PropTypes.string,
    style: PropTypes.any,
    type: PropTypes.string,
    wrapperClassName: PropTypes.string,
    rounded: PropTypes.bool,
    stretch: PropTypes.bool,
    tall: PropTypes.bool,
    icon: PropTypes.any,
    iconSize: PropTypes.string,
    iconOnly: PropTypes.bool,
    isFixedTooltip: PropTypes.bool,
    theme: PropTypes.oneOf(['default', 'alert']),
    tooltip: PropTypes.any,
    tooltipClassName: PropTypes.string,
    tooltipPosition: PropTypes.string,
    tabIndex: PropTypes.number,
};
